// How the analysis reads (/analysis, docs/GAME-DIAGNOSIS.md §4.2): the names and the explanations of the
// causes and the kinds of error, the opening tree it groups by, and the small formats every section
// shares. The numbers come from /api/diagnosis already added up; this decides how they are told.
//
// Pure: no fetch, no DOM. The page paints what this returns.

import { pace } from "./adminQueue.ts";
import { t } from "../i18n/analysis.ts";

// -------------------------------------------------------------------------------------------------
// The words
// -------------------------------------------------------------------------------------------------

export type Cause =
    | "conversion" | "flagged" | "opening" | "time_pressure_blunder" | "blunder" | "king_attack" | "slow_slide"
    | "unexplained" | "level_draw" | "won";

export type Kind = "mate_allowed" | "hung_material" | "missed_reply" | "many_replies" | "out_of_time";

export type ClockShare = "flag" | "trouble" | "mutual";

export interface Term {
    label: string;
    help: string;
    color: string;
}

/** A term whose words are read in the page's language each time they are asked for. */
type Word = Parameters<typeof t>[0];
const say = (key: Word): string => (t as (key: Word) => string)(key);
const term = (label: Word, help: Word, color: string): Term => ({
    get label() { return say(label); },
    get help() { return say(help); },
    color,
});

/** In the order the report lists them: where a game goes wrong first, then how, then the clock. */
export const CAUSES: Record<Cause, Term> = {
    opening: term("causeOpening", "causeOpeningHelp", "#8e24aa"),
    blunder: term("causeBlunder", "causeBlunderHelp", "#c62828"),
    time_pressure_blunder: term("causeTimeTrouble", "causeTimeTroubleHelp", "#ef6c00"),
    flagged: term("causeFlagged", "causeFlaggedHelp", "#f9a825"),
    conversion: term("causeConversion", "causeConversionHelp", "#1565c0"),
    slow_slide: term("causeSlide", "causeSlideHelp", "#00897b"),
    king_attack: term("causeKing", "causeKingHelp", "#6d4c41"),
    unexplained: term("causeUnexplained", "causeUnexplainedHelp", "#90a4ae"),
    level_draw: term("causeLevelDraw", "causeLevelDrawHelp", "#b0bec5"),
    won: term("causeWon", "causeWonHelp", "#2e7d32"),
};

export const CAUSE_ORDER: Cause[] = ["opening", "blunder", "time_pressure_blunder", "flagged", "conversion",
    "slow_slide", "king_attack", "unexplained", "level_draw", "won"];

// -------------------------------------------------------------------------------------------------
// Which games the page reads: the whole report is about one result, or all of them
// -------------------------------------------------------------------------------------------------

export type ResultChoice = "loss" | "win" | "draw" | "all";

const choice = (key: ResultChoice, label: Word, noun: Word) => ({
    key,
    get label() { return say(label); },
    get noun() { return say(noun); },
});

export const RESULT_CHOICES: { key: ResultChoice; label: string; noun: string }[] = [
    choice("loss", "losses", "lossesNoun"),
    choice("win", "wins", "winsNoun"),
    choice("draw", "draws", "drawsNoun"),
    choice("all", "all", "gamesNoun"),
];

/** What the chosen games are called in a sentence: "your 120 analysed losses". */
export const resultNoun = (result: ResultChoice) => RESULT_CHOICES.find((choice) => choice.key === result)!.noun;

export const KINDS: Record<Kind, Term> = {
    mate_allowed: term("kindMate", "kindMateHelp", "#4a148c"),
    hung_material: term("kindHung", "kindHungHelp", "#c62828"),
    missed_reply: term("kindMissed", "kindMissedHelp", "#ef6c00"),
    many_replies: term("kindMany", "kindManyHelp", "#1565c0"),
    out_of_time: term("kindTime", "kindTimeHelp", "#f9a825"),
};

export const KIND_ORDER: Kind[] = ["mate_allowed", "hung_material", "missed_reply", "many_replies", "out_of_time"];

/** The column both opening tables lead with. */
export const WENT_WRONG: Term = term("wentWrong", "wentWrongHelp", "#c62828");

export const ONLY_MOVE: Term = term("onlyMove", "onlyMoveHelp", "#546e7a");

export const CLOCKS: Record<ClockShare, Term> = {
    flag: term("clockFlag", "clockFlagHelp", "#f9a825"),
    trouble: term("clockTrouble", "clockTroubleHelp", "#ef6c00"),
    mutual: term("clockMutual", "clockMutualHelp", "#8d6e63"),
};

export const TERMINATIONS: Record<string, string> = {
    get mate() { return say("endMate"); },
    get resign() { return say("endResign"); },
    get time() { return say("endTime"); },
    get abandon() { return say("endAbandon"); },
    get draw_agreed() { return say("endDrawAgreed"); },
    get repetition() { return say("endRepetition"); },
    get stalemate() { return say("endStalemate"); },
    get insufficient() { return say("endInsufficient"); },
    get fifty() { return say("endFifty"); },
    get time_vs_insufficient() { return say("endTimeVsInsufficient"); },
    get other() { return say("endOther"); },
};

export const causeTerm = (cause: string): Term => CAUSES[cause as Cause]
    ?? { label: cause, help: "", color: "#90a4ae" };
export const kindTerm = (kind: string): Term => KINDS[kind as Kind] ?? { label: kind, help: "", color: "#90a4ae" };

// -------------------------------------------------------------------------------------------------
// Numbers
// -------------------------------------------------------------------------------------------------

export const pct = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : "–");

/**
 * A score in pawns, from the user's side. A mate is written as one: the scores keep it as
 * (21 - distance) x 100, which read as pawns would say +17.
 */
export function pawns(cp: number | null | undefined): string {
    if (cp === null || cp === undefined) return "–";
    if (Math.abs(cp) >= 1100) return cp > 0 ? say("mate") : say("mated");
    return `${cp > 0 ? "+" : ""}${(cp / 100).toFixed(1)}`;
}

/** "24. Nd2" or "24... Nd2" for ply 46 or 47. */
export function moveLabel(ply: number, san: string | null | undefined): string {
    return `${Math.floor(ply / 2) + 1}.${ply % 2 ? ".." : ""} ${san ?? ""}`.trim();
}

/**
 * Where to look at a game, at a move of it, on the site it was played on. Both sites count the same way:
 * lichess's `#N` and chess.com's `?move=N` show the position after N half moves, so the 0-indexed ply of the
 * move becomes N = ply + 1 — the position the move left.
 */
export function gameLink(provider: string, providerGameId: string, color: string, ply?: number | null): string {
    if (provider === "LICHESS") {
        return `https://lichess.org/${providerGameId}/${color}${ply === null || ply === undefined ? "" : `#${ply + 1}`}`;
    }
    return `https://www.chess.com/game/live/${providerGameId}${ply === null || ply === undefined ? "" : `?move=${ply + 1}`}`;
}

// -------------------------------------------------------------------------------------------------
// Counting what the summary says
// -------------------------------------------------------------------------------------------------

export interface Pair {
    first: string;
    second: string;
    games: number;
}

export interface Bar {
    key: string;
    term: Term;
    games: number;
    share: number;
}

/** Bars, in the order given, of the keys that have games; share is the percentage of `total`. */
export function bars(counts: Record<string, number>, order: string[], term: (key: string) => Term,
                     total = Object.values(counts).reduce((sum, n) => sum + n, 0)): Bar[] {
    const keys = [...order, ...Object.keys(counts).filter((key) => !order.includes(key))];
    return keys.filter((key) => counts[key]).map((key) => ({
        key, term: term(key), games: counts[key]!, share: total ? (counts[key]! / total) * 100 : 0,
    }));
}

/** The second keys of the pairs whose first key is `first`, added up: the causes of the losses, say. */
export function countsOf(pairs: Pair[], first?: string): Record<string, number> {
    return pairs.filter((pair) => first === undefined || pair.first === first)
        .reduce<Record<string, number>>((counts, pair) => {
            counts[pair.second] = (counts[pair.second] ?? 0) + pair.games;
            return counts;
        }, {});
}

// -------------------------------------------------------------------------------------------------
// How the opening was left: a state of its own, beside the cause (gameDiagnosis.ts:openingExit)
// -------------------------------------------------------------------------------------------------

export type Exit = "worse" | "level" | "better" | "inside";

export const EXITS: Record<Exit, Term> = {
    worse: term("exitWorse", "exitWorseHelp", "#8e24aa"),
    level: term("exitLevel", "exitLevelHelp", "#90a4ae"),
    // Green and not the blue it was: on /analysis blue means "analysed by Stockfish"
    better: term("exitBetter", "exitBetterHelp", "#2e7d32"),
    inside: term("exitInside", "exitInsideHelp", "#c62828"),
};

export const EXIT_ORDER: Exit[] = ["worse", "inside", "level", "better"];

export const exitTerm = (exit: string): Term => EXITS[exit as Exit] ?? { label: exit, help: "", color: "#90a4ae" };

/** The opening went wrong: out of it worse, or the game never got out of it. */
const wrong = (exits: Record<string, number>) => (exits.worse ?? 0) + (exits.inside ?? 0);

export interface ExitView {
    /** Analysed games whose way out of the opening is known. */
    games: number;
    byExit: Record<string, number>;
    /** Games the opening went wrong in. */
    wrong: number;
    /** Games out of the opening level or better: the opening was not the problem... */
    fine: number;
    /** ...and what decided them. */
    fineCauses: Record<string, number>;
}

/** How the analysed games left the opening, and what decided the ones that left it well, from (exit, cause) pairs. */
export function exitView(pairs: Pair[]): ExitView {
    const byExit = countsOf(pairs.map((pair) => ({ first: "", second: pair.first, games: pair.games })));
    const fineCauses = countsOf(pairs.filter((pair) => pair.first === "level" || pair.first === "better")
        .map((pair) => ({ first: "", second: pair.second, games: pair.games })));
    const games = Object.values(byExit).reduce((sum, n) => sum + n, 0);
    const fine = (byExit.level ?? 0) + (byExit.better ?? 0);
    return { games, byExit, wrong: wrong(byExit), fine, fineCauses };
}

/**
 * How often the opening went wrong in each result, from (result, exit) pairs: the wins are the yardstick.
 * Going wrong in 26% of the losses says little alone; 26% against 10% of the wins says the opening costs
 * games, and 26% against 24% says it does not.
 */
export function wrongByResult(pairs: Pair[]): { result: string; games: number; wrong: number; share: number }[] {
    return (["loss", "draw", "win"] as const).map((result) => {
        const exits = countsOf(pairs, result);
        const games = Object.values(exits).reduce((sum, n) => sum + n, 0);
        const went = wrong(exits);
        return { result, games, wrong: went, share: games ? went / games : 0 };
    }).filter((row) => row.games > 0);
}

// -------------------------------------------------------------------------------------------------
// Openings, three levels deep: the family, the variation and what comes after it in the name
// ("Sicilian Defense" / "Najdorf Variation" / "English Attack"). Over the analysed games only.
// -------------------------------------------------------------------------------------------------

export interface OpeningRow {
    /** The colour the user had: the server sends one row per opening and colour. */
    color?: "white" | "black";
    opening: string | null;
    /** Analysed games, of the result the page reads. */
    games: number;
    losses: number;
    draws: number;
    wins: number;
    /** The analysed losses, by cause. */
    causes: Record<string, number>;
    /** The analysed games, by how the opening was left. */
    exits: Record<string, number>;
    /** The score out of the opening added up, each held to ±10, over `exitScored` games. */
    exitCpTotal: number;
    exitScored: number;
}

export interface OpeningNode {
    /** The full name up to this level: what the games list filters by. */
    key: string;
    /** What this level adds to the one above. */
    label: string;
    depth: number;
    games: number;
    losses: number;
    causes: Record<string, number>;
    exits: Record<string, number>;
    exitCpTotal: number;
    exitScored: number;
    /** Games the opening went wrong in beyond what the user's average says it would: what ranks the tree. */
    excess: number;
    children: OpeningNode[];
}

export const UNNAMED = "Unnamed opening";

/** The keys of the levels a name hangs from, shortest first. */
export function openingPath(name: string | null): string[] {
    if (!name) return [UNNAMED];
    const colon = name.indexOf(":");
    if (colon < 0) return [name];
    const family = name.slice(0, colon);
    const parts = name.slice(colon + 1).trim().split(/,\s*/);
    const variation = `${family}: ${parts[0]}`;
    return parts.length > 1 ? [family, variation, name] : [family, variation];
}

function labelOf(key: string, depth: number): string {
    if (depth === 0) return key;
    const cut = key.lastIndexOf(depth === 1 ? ": " : ", ");
    return cut < 0 ? key : key.slice(cut + 2);
}

const add = (into: Record<string, number>, from: Record<string, number>) => Object.entries(from)
    .forEach(([key, games]) => {
        into[key] = (into[key] ?? 0) + games;
    });

/** How often the opening goes wrong for this player, over every analysed game whose way out is known. */
export function wrongShare(rows: { exits: Record<string, number> }[]): number {
    const known = rows.reduce((sum, row) => sum + Object.values(row.exits).reduce((n, games) => n + games, 0), 0);
    return known ? rows.reduce((sum, row) => sum + wrong(row.exits), 0) / known : 0;
}

/**
 * The openings as a tree, each level added up from the ones under it. Ranked by what each one costs beyond
 * the user's own average: the games the opening went wrong in, less the ones it would have at the user's
 * usual rate. A big opening is not costly because it is big — its losses are mostly the ones any opening
 * would have — and one that goes wrong half as often as usual sinks to the bottom, which is the answer too.
 */
export function openingTree(rows: OpeningRow[]): OpeningNode[] {
    const usual = wrongShare(rows);
    const root = node("", "", -1);
    for (const row of rows) {
        let parent = root;
        openingPath(row.opening).forEach((key, depth) => {
            let child = parent.children.find((candidate) => candidate.key === key);
            if (!child) {
                child = node(key, labelOf(key, depth), depth);
                parent.children.push(child);
            }
            child.games += row.games;
            child.losses += row.losses;
            add(child.causes, row.causes);
            add(child.exits, row.exits);
            child.exitCpTotal += row.exitCpTotal;
            child.exitScored += row.exitScored;
            parent = child;
        });
    }
    const rank = (nodes: OpeningNode[]) => {
        nodes.forEach((each) => {
            const known = Object.values(each.exits).reduce((sum, games) => sum + games, 0);
            each.excess = wrong(each.exits) - known * usual;
            rank(each.children);
        });
        nodes.sort((a, b) => b.excess - a.excess || b.losses - a.losses || b.games - a.games
            || a.label.localeCompare(b.label));
    };
    rank(root.children);
    return root.children;
}

function node(key: string, label: string, depth: number): OpeningNode {
    return { key, label, depth, games: 0, losses: 0, causes: {}, exits: {}, exitCpTotal: 0, exitScored: 0, excess: 0, children: [] };
}

/** The games of a node the opening went wrong in, and their share of the ones whose way out is known. */
export function wentWrong(node: { exits: Record<string, number> }): { games: number; share: number } {
    const known = Object.values(node.exits).reduce((sum, games) => sum + games, 0);
    const games = wrong(node.exits);
    return { games, share: known ? games / known : 0 };
}

/** The average score out of the opening, in centipawns, of the games that got out of it; null with none. */
export function averageExit(node: { exitCpTotal: number; exitScored: number }): number | null {
    return node.exitScored ? Math.round(node.exitCpTotal / node.exitScored) : null;
}

/** How far over the usual rate, and on how many games, an opening has to be before the report says so. */
export const COSTLY = { minGames: 3, overUsual: 0.15 } as const;

/** The opening goes wrong for the user clearly more often than their openings usually do. */
export function costly(node: { exits: Record<string, number> }, usual: number): boolean {
    const { games, share } = wentWrong(node);
    return games >= COSTLY.minGames && share >= usual + COSTLY.overUsual;
}

/** The cause with most analysed losses in a node, and how many of them. */
export function mainCause(causes: Record<string, number>): { cause: string; games: number } | null {
    const [cause, games] = Object.entries(causes).sort((a, b) => b[1] - a[1])[0] ?? [];
    return cause === undefined ? null : { cause, games: games! };
}

/** The tree cut to what is worth a row: nodes with fewer games than the floor of their level go together. */
export function visibleChildren(nodes: OpeningNode[], minGames: number): { shown: OpeningNode[]; rest: OpeningNode[] } {
    return { shown: nodes.filter((node) => node.games >= minGames), rest: nodes.filter((node) => node.games < minGames) };
}

export interface ExitNode {
    key: string;
    label: string;
    /** Games whose way out of the opening is known. */
    games: number;
    exits: Record<string, number>;
    /** Those the opening went wrong in: out of it worse, or over inside it. */
    wrong: number;
    /** The score out of the opening added up, each held to ±10, over `exitScored` games: what Stockfish said. */
    exitCpTotal: number;
    exitScored: number;
    children: ExitNode[];
}

/**
 * How the games came out of the opening, by opening and then by variation — every one of them, however
 * few games it has: this is where somebody looks up the opening they have in mind, not a ranking. The
 * ones that went wrong most often come first.
 */
export function exitsByOpening(rows: OpeningRow[]): ExitNode[] {
    const root: ExitNode = {
        key: "", label: "", games: 0, exits: {}, wrong: 0, exitCpTotal: 0, exitScored: 0, children: [],
    };
    for (const row of rows) {
        const games = Object.values(row.exits).reduce((sum, n) => sum + n, 0);
        if (!games) continue;
        let parent = root;
        openingPath(row.opening).forEach((key, depth) => {
            let child = parent.children.find((candidate) => candidate.key === key);
            if (!child) {
                child = {
                    key, label: labelOf(key, depth), games: 0, exits: {}, wrong: 0, exitCpTotal: 0,
                    exitScored: 0, children: [],
                };
                parent.children.push(child);
            }
            child.games += games;
            add(child.exits, row.exits);
            child.wrong = wrong(child.exits);
            // What Stockfish said, carried up the tree: a name is its variations added together
            child.exitCpTotal += row.exitCpTotal;
            child.exitScored += row.exitScored;
            parent = child;
        });
    }
    const rank = (nodes: ExitNode[]) => {
        nodes.sort((a, b) => b.wrong - a.wrong || b.games - a.games || a.label.localeCompare(b.label));
        nodes.forEach((each) => rank(each.children));
    };
    rank(root.children);
    return root.children;
}

// -------------------------------------------------------------------------------------------------
// Below the names, by the moves: where the opening table stops telling games apart ("Sicilian Defense:
// Closed" is every 2.Nc3), the games themselves still do. Optional on the page: somebody starting out
// wants the names, somebody with a repertoire wants the branch.
// -------------------------------------------------------------------------------------------------

/** One analysed game's opening, as /openings/lines hands it over: its first twelve moves each, in SAN. */
export interface OpeningLine {
    color: "white" | "black";
    opening: string;
    result: string;
    cause: string;
    exit: string | null;
    /** The score out of the opening, held to ±10; null inside it. */
    exitCp: number | null;
    moves: string;
}

export interface MoveNode {
    /** The moves from the start, in SAN: what the games list filters by. */
    moves: string;
    /** The moves since the row above, numbered: "3.f4 e6". */
    label: string;
    /** Half moves from the start. */
    ply: number;
    games: number;
    losses: number;
    /** The losses, by cause. */
    causes: Record<string, number>;
    exits: Record<string, number>;
    wrong: number;
    exitCpTotal: number;
    exitScored: number;
    children: MoveNode[];
    /** Branches of fewer games than a branch needs: shown on request, under one row that says how many. */
    few: MoveNode[];
    /** The games in `few`. */
    rest: number;
}

/** A branch needs this many games: one is a game, not a line. */
export const MIN_BRANCH_GAMES = 2;

/** The games of each name and colour, as `${color}|${opening}`: a name's own games, not its children's. */
export function linesByName(lines: OpeningLine[]): Map<string, OpeningLine[]> {
    const byName = new Map<string, OpeningLine[]>();
    for (const line of lines) {
        const key = `${line.color}|${line.opening}`;
        byName.set(key, [...(byName.get(key) ?? []), line]);
    }
    return byName;
}

/** SAN moves from `from` on, numbered the way a scoresheet would: "3.f4 e6", or "3...e6" from Black's. */
export function numberedSince(sans: string[], from: number): string {
    return sans.slice(from).map((san, index) => {
        const ply = from + index;
        const number = Math.floor(ply / 2) + 1;
        if (ply % 2 === 0) return `${number}.${san}`;
        return index === 0 ? `${number}...${san}` : san;
    }).join(" ");
}

/**
 * The games of one name split by their moves, where they stop being the same game: the moves all of them
 * share go together, and at the first move they do not, each move with enough games is a branch, which
 * splits again the same way. It splits wherever they part — at the opponent's answer or at the user's own
 * choice, both are something to know. Branches of a single game go in `few`: there, but not a branch.
 */
export function moveBranches(lines: Partial<OpeningLine>[], usual?: number):
    { children: MoveNode[]; few: MoveNode[]; rest: number } {
    const games = lines.map((line) => ({ ...line, sans: line.moves ? line.moves.split(" ") : [] }));
    if (!games.length) return { children: [], few: [], rest: 0 };
    const { children, few, rest } = split(games, 0, 0, usual);
    return { children, few, rest };
}

type Played = Partial<OpeningLine> & { sans: string[] };

/**
 * With the user's usual rate, branches rank as the opening tree does — by the games gone wrong beyond what
 * that rate would give; without it, by the games gone wrong.
 */
function split(games: Played[], from: number, parentPly: number, usual?: number): MoveNode {
    // Past what they all share
    let ply = from;
    while (games.every((game) => game.sans.length > ply && game.sans[ply] === games[0]!.sans[ply])) ply++;
    const exits: Record<string, number> = {};
    const causes: Record<string, number> = {};
    let losses = 0;
    let exitCpTotal = 0;
    let exitScored = 0;
    games.forEach((game) => {
        if (game.exit) exits[game.exit] = (exits[game.exit] ?? 0) + 1;
        if (game.result === "loss") {
            losses++;
            if (game.cause) causes[game.cause] = (causes[game.cause] ?? 0) + 1;
        }
        if (game.exitCp !== null && game.exitCp !== undefined) {
            exitCpTotal += game.exitCp;
            exitScored++;
        }
    });
    const sans = games[0]?.sans ?? [];
    const node: MoveNode = {
        moves: sans.slice(0, ply).join(" "), label: numberedSince(sans.slice(0, ply), parentPly), ply,
        games: games.length, losses, causes, exits, wrong: wrong(exits), exitCpTotal, exitScored, children: [],
        few: [], rest: 0,
    };
    const byMove = new Map<string, Played[]>();
    games.filter((game) => game.sans.length > ply).forEach((game) => {
        byMove.set(game.sans[ply]!, [...(byMove.get(game.sans[ply]!) ?? []), game]);
    });
    for (const branch of byMove.values()) {
        const child = split(branch, ply + 1, ply, usual);
        if (branch.length >= MIN_BRANCH_GAMES) node.children.push(child);
        else node.few.push(child);
    }
    node.rest = node.few.reduce((sum, branch) => sum + branch.games, 0);
    const rank = (branch: MoveNode) => (usual === undefined ? branch.wrong
        : branch.wrong - Object.values(branch.exits).reduce((sum, n) => sum + n, 0) * usual);
    const order = (a: MoveNode, b: MoveNode) => rank(b) - rank(a) || b.games - a.games || a.label.localeCompare(b.label);
    node.children.sort(order);
    node.few.sort(order);
    return node;
}

// -------------------------------------------------------------------------------------------------
// Asking for an analysis
// -------------------------------------------------------------------------------------------------

/** What the page offers to analyse: always the newest games not analysed yet, as many as chosen. */
export const ANALYSIS_CHOICES: { key: string; label: string; limit: number | null }[] = [
    { key: "50", get label() { return say("last50"); }, limit: 50 },
    { key: "100", get label() { return say("last100"); }, limit: 100 },
    { key: "all", get label() { return say("allOfThem"); }, limit: null },
];

/** How long a number of seconds is, said the way a person would wait for it. */
export function durationLabel(seconds: number): string {
    if (seconds < 60) return say("underAMinute");
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return t("aboutMinutes", minutes);
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return t("aboutHours", hours, rest);
}

export interface JobProgress {
    status: string;
    gamesTotal: number | null;
    gamesDone: number;
    positionsTotal: number | null;
    positionsDone: number;
    startedAt?: string | null;
}

/**
 * How far an analysis is, 0 to 100: scoring the positions is almost all of the work, so it fills nine
 * tenths of the bar, and writing the games down the last one.
 */
export function jobPercent(job: JobProgress): number {
    if (job.status === "done") return 100;
    const positions = job.positionsTotal ? Math.min(1, job.positionsDone / job.positionsTotal) : 0;
    const games = job.gamesTotal ? Math.min(1, job.gamesDone / job.gamesTotal) : 0;
    return Math.round(positions * 90 + games * 10);
}

export const isRunning = (job: { status: string } | null | undefined) =>
    !!job && (job.status === "queued" || job.status === "running");

/**
 * What is left of a running job at its own pace — "about 4 minutes" — or null until there is a pace to read:
 * the first half minute, a queued job, and a total not yet counted all keep quiet rather than guess.
 */
export function jobTimeLeft(job: JobProgress, now: Date): string | null {
    if (job.status !== "running") return null;
    const rate = pace(job, now.toISOString());
    return rate?.etaSeconds == null ? null : durationLabel(rate.etaSeconds);
}
